/**
 * Russian UI dictionary. **This file is the canonical source of truth for the
 * set of translation keys** — `strings.ro.ts` is typed as `typeof ru`, so any
 * key added here must also be added there (TS catches the mismatch at build).
 *
 * Plural entries use `Intl.PluralRules("ru")` buckets:
 *   one  — 1, 21, 31, …
 *   few  — 2–4, 22–24, …
 *   many — 0, 5–20, 25–30, …
 * Romanian uses `one` / `few` / `other` — keep the plural-key names consistent
 * with `Intl.PluralRules` so the same `select(n)` result maps to both dicts.
 *
 * Tone: polite (вы-form), concise.
 */
export const ru = {
  /* ─── Bottom nav ─────────────────────────── */
  "nav.home": "Главная",
  "nav.study": "Учить",
  "nav.vocab": "Слова",
  "nav.grammar": "Грамматика",
  "nav.progress": "Прогресс",
  "nav.admin": "Админ",
  "nav.levels": "Уровни",
  "nav.settings": "Настройки",

  /* ─── Common ─────────────────────────────── */
  "common.loading": "Загрузка…",
  "common.failed_to_load": "Не удалось загрузить",
  "common.cancel": "Отмена",
  "common.confirm": "Подтвердить",
  "common.back": "Назад",
  "common.save": "Сохранить",
  "common.saving": "Сохранение…",
  "common.all": "Все",
  "common.new": "Новые",
  "common.learning": "В процессе",
  "common.known": "Выучено",
  "common.min": "мин",
  "common.yes": "Да",
  "common.loading_more": "Загрузка…",

  /* ─── Home ───────────────────────────────── */
  "home.greeting": "Привет",
  "home.greeting_with_name": "Привет, {name}",
  "home.due_today": "На сегодня",
  "home.cards": "карточек",
  "home.about_minutes": "~{n} мин",
  "home.all_caught_up": "Всё повторено",
  "home.start_session": "Начать занятие",
  "home.new_count": "{n} новых",
  "home.reviews_count": "{n} на повторение",
  "home.stat_known": "Выучено",
  "home.stat_learning": "В процессе",
  "home.stat_studied": "Изучено",
  // Split session CTAs (words / grammar) — mixed study mode is retired.
  // {n} = due cards of that flavor.
  "home.study_words": "Слова · {n}",
  "home.study_grammar": "Грамматика · {n}",
  "home.grammar_title": "Грамматика",
  "home.see_all": "Все",
  "home.grammar_new": "новое",
  "home.grammar_review": "повторить",
  "home.grammar_known": "выучено",
  "home.vocabulary": "Словарь",
  "home.browse_words": "Просмотреть {n} румынских слов",
  // Streak chip in the page header — only rendered when n > 0.
  "home.streak_aria": "Серия: {n} дней",
  // Mode switch (segmented control in the header).
  "home.mode_open_label": "Всё",
  "home.mode_open_sub": "Свободно",
  "home.mode_level_sub": "Ур.",
  // Footer hint cards explaining the active mode + how to switch.
  "home.mode_hint_open_title": "Открытый режим",
  "home.mode_hint_open_body": "Все уровни вперемешку. Переключитесь на «Уровень» для пошагового пути.",
  "home.mode_hint_level_title": "Режим уровней",
  "home.mode_hint_level_body": "Только материал {level}. Прогресс общий с открытым режимом.",
  // Trailing link in the ModeHint card. Routes to /settings where the
  // user can flip mode. Was inline ModeSwitch in the header before.
  "home.mode_hint_switch_link": "Сменить в настройках",
  // Level home — hero card.
  "home.path": "Путь",
  "home.cards_to_level_up": "{n} карточек до повышения",
  // Shown next to the CTA when the user has cleared 85% on both rings.
  // Phrased exam-agnostic for now — Phase 7 (the readiness exam) is
  // deferred pending a richer task-type vocabulary. When the exam ships,
  // swap callsites back to `ready_for_exam` / `cta_final_exam` (kept).
  "home.ready_to_advance": "Готов к следующему уровню",
  "home.ready_for_exam": "Готов к экзамену!",
  "home.bar_vocab": "ЛЕКСИКА",
  "home.bar_grammar": "ГРАММАТИКА",
  // Primary CTA on the level hero — three variants.
  "home.cta_continue": "Продолжить {level}",
  "home.cta_continue_with_new": "Продолжить {level} · {n} новых",
  "home.cta_final_exam": "Финальный экзамен · {level}",
  // "Today in {level}" breakdown card.
  "home.today_in_level": "Сегодня в {level}",
  "home.row_new_vocab": "Новая лексика",
  "home.row_reviews": "Повторения",
  "home.row_grammar": "Грамматика",

  /* ─── Words list ─────────────────────────── */
  "words.title": "Слова",
  "words.add": "Добавить",
  "words.totals": "{total} всего · {known} выучено · {learning} в процессе",
  "words.search_placeholder": "Поиск слов, переводов…",
  "words.section_all": "Все слова",
  "words.showing_of": "показано {shown} из {total}",
  "words.empty_no_match": "Нет слов, подходящих под «{q}»",
  "words.empty_no_match_filtered": "Нет слов, подходящих под «{q}» в категории «{filter}».",
  "words.empty_filter": "Пока нет слов в этой категории.",
  "words.empty_all": "Пока нет слов. Импортируйте JSON или запустите сид-скрипт.",
  "words.filter_by_tag": "Фильтр по тегу:",
  "words.clear_filter": "Сбросить",

  /* ─── Word detail ────────────────────────── */
  "word_detail.not_found": "Слово не найдено",
  "word_detail.back_aria": "Назад к словам",
  "word_detail.translation": "Перевод",
  "word_detail.translations": "Переводы",
  "word_detail.also_prefix": "также:",
  "word_detail.forms": "Формы",
  "word_detail.forms_indefinite": "без артикля",
  "word_detail.forms_articulated": "с артиклем",
  "word_detail.forms_singular": "ед. число",
  "word_detail.forms_plural": "мн. число",
  "word_detail.your_progress": "Ваш прогресс",
  "word_detail.examples": "Примеры",
  "word_detail.synonyms": "Синонимы",
  "word_detail.tags": "Теги",
  "word_detail.edit": "Редактировать",
  // Mark-known CTA. Two variants depending on the user's progress on
  // this word: `_new` reads as "I already know this" (no progress yet);
  // `_learning` reads as "graduate from the learning stage." Hidden
  // entirely once state === "known".
  "word_detail.mark_known_new": "Я знаю это слово",
  "word_detail.mark_known_learning": "Знаю — отметить как выученное",
  // {lemma} is the Romanian word the user is viewing. Browser confirm()
  // returns true on OK / false on Cancel.
  "word_detail.mark_known_confirm":
    "Отметить «{lemma}» как выученное? Слово появится снова через ~4 месяца для проверки.",
  "word_detail.delete_confirm": "Удалить это слово и все связи?",
  "word_detail.delete_yes": "Да, удалить",
  "word_detail.deleting": "Удаление…",
  "word_detail.delete_action": "Удалить слово",
  "word_detail.no_image": "без фото",
  "word_detail.next_review_overdue": "просрочено на {n}д",
  "word_detail.next_review_today": "на сегодня",
  "word_detail.next_review_days": "через {n}д",
  "word_detail.next_review_months": "через {n}мес",
  "word_detail.related_grammar": "Соответствующая грамматика",
  "word_detail.related_grammar_show_more": "Показать ещё {n}",
  "word_detail.related_grammar_show_less": "Свернуть",
  "word_detail.matched_by_matchers": "по признаку",
  "word_detail.matched_by_lemma": "напрямую",
  "word_detail.matched_by_tags": "по теме",

  /* ─── Inflection card (all POS) ─────────────── */
  "inflection.indefinite": "без артикля",
  "inflection.articulated": "с артиклем",
  "inflection.singular": "ед. число",
  "inflection.plural": "мн. число",
  "inflection.masculine": "муж. род",
  "inflection.feminine": "жен. род",
  "inflection.m_short": "м.",
  "inflection.f_short": "ж.",
  "inflection.n_short": "ср.",
  "inflection.verb_prezent_1sg": "наст. 1 л. ед.",
  "inflection.verb_prezent_3sg": "наст. 3 л. ед.",
  "inflection.verb_participiu": "причастие прош.",
  "inflection.verb_imperativ_2sg": "повел. 2 л. ед.",
  "inflection.verb_imperfect_3sg": "имперф. 3 л. ед.",
  "inflection.verb_perfect_3sg": "перф. 3 л. ед.",
  "inflection.pron_nominativ": "именительный",
  "inflection.pron_acc_stressed": "винит. (удар.)",
  "inflection.pron_acc_unstressed": "винит. (безудар.)",
  "inflection.pron_dat_stressed": "дат. (удар.)",
  "inflection.pron_dat_unstressed": "дат. (безудар.)",
  "inflection.conjugation_label": "Спряжение",
  "inflection.conjugation_value": "{n} спряж.",
  "inflection.reflexive_badge": "возвратный",
  "inflection.impersonal_badge": "безличный",
  "inflection.agreement_four_forms": "4 формы",
  "inflection.agreement_two_forms": "2 формы",
  "inflection.agreement_invariable": "несклоняемое",
  "inflection.gender_f": "ж. р.",
  "inflection.gender_m": "м. р.",
  "inflection.gender_n": "ср. р.",

  /* ─── Grammar detail — Applies-to section ──── */
  "grammar_detail.applies_to": "Применяется к",
  "grammar_detail.applies_none": "Нет связанных слов.",
  "grammar_detail.applies_count": "{n}",
  "grammar_detail.applies_more": "+{n} ещё",
  "grammar_detail.related_topics": "Связанные темы",

  /* ─── Grammar detail ─────────────────────── */
  "grammar_detail.not_found": "Правило не найдено",
  "grammar_detail.back_aria": "Назад к грамматике",
  // "Quiz yourself" entry point on GrammarDetail (Phase 5a). Visible
  // only when the rule has authored quiz items.
  "grammar_detail.quiz_yourself_title": "Проверьте себя",
  "grammar_detail.quiz_yourself_subtitle": "{n} карточек на это правило",
  "grammar_detail.practice": "Практика",
  // Mirror of word_detail.mark_known_*: `_new` = no progress yet,
  // `_learning` = graduate past remaining reviews. Hidden once
  // state === "known".
  "grammar_detail.mark_known_new": "Я знаю эту тему",
  "grammar_detail.mark_known_learning": "Знаю — отметить как выученную",
  // {title} is the rule title. Browser confirm() OK/Cancel.
  "grammar_detail.mark_known_confirm":
    "Отметить «{title}» как выученную? Тема появится снова через ~4 месяца для проверки.",

  /* ─── Practice session (Phase 5a) ──────────────────── */
  "practice.mode_mcq": "MCQ",
  "practice.mode_type": "Ввод",
  "practice.load_error": "Не удалось загрузить упражнения.",
  "practice.empty_title": "Пока нет упражнений",
  "practice.empty_body": "К правилу «{rule}» ещё не добавлены практические карточки.",
  "practice.done_title": "Готово!",
  "practice.done_body": "Вы прошли все {n} карточек по правилу «{rule}».",
  "practice.do_again": "Пройти снова",

  /* ─── Level-up exam (Phase 7) ─────────────── */
  // Lobby chrome
  "exam.lobby_eyebrow": "Экзамен на {level}",
  "exam.lobby_title": "Финальный экзамен",
  "exam.lobby_hero_title": "Готовность к {level}",
  "exam.lobby_hero_sub": "{count} вопросов · проходной балл {pass}%",
  // Section labels — match `EXAM_SECTIONS` order/keys.
  "exam.section_vocab_mcq": "Лексика — выбор",
  "exam.section_vocab_type": "Лексика — ввод",
  "exam.section_grammar_mcq": "Грамматика — выбор",
  "exam.section_grammar_fill": "Грамматика — пропуск",
  "exam.section_grammar_transform": "Грамматика — преобразование",
  // Rules block
  "exam.rule_pass_mark": "Нужно набрать ≥{pass}%, чтобы пройти.",
  "exam.rule_cooldown": "При неудаче можно повторить через {hours} ч.",
  "exam.rule_no_srs": "Экзамен не влияет на расписание повторений.",
  // Buttons
  "exam.start_button": "Начать экзамен · {level}",
  "exam.starting": "Запускаем…",
  "exam.submit": "Завершить",
  "exam.submitting": "Проверяем…",
  "exam.next": "Дальше",
  "exam.prev": "Назад",
  "exam.flag": "Отметить для проверки",
  "exam.unflag": "Снять отметку",
  // Session
  "exam.session_title": "Экзамен",
  "exam.type_placeholder": "Ваш ответ",
  // Confirm dialog when the user taps the back arrow mid-exam.
  // Native window.confirm — clearer than dropping silently. Uses \n
  // so the prompt's title + body land on separate lines in iOS modal.
  "exam.abandon_confirm": "Прервать экзамен? Прогресс будет потерян.",
  // Errors
  "exam.error_not_ready": "Сначала достигните 85% в лексике и грамматике.",
  "exam.error_cooldown": "Повторить экзамен можно примерно через {hours} ч.",
  "exam.error_generic": "Не удалось запустить экзамен. Попробуйте позже.",
  // Results
  "exam.result_pass_title": "Поздравляем — вы перешли на {level}!",
  "exam.result_pass_sub": "Уровень {level} открыт. Можно продолжать.",
  "exam.result_fail_title": "Пока не хватает баллов",
  "exam.result_fail_sub": "Нужно ≥{pass}%. Подучите слабые темы и попробуйте снова.",
  "exam.result_breakdown_title": "По разделам",
  "exam.result_cta_continue": "Продолжить · {level}",
  "exam.result_cta_retry_later": "Вернуться к уровню",
  "exam.result_cta_home": "На главную",
  // Weak-area tip — only rendered on fail. Picks the lowest-scoring
  // section and points the user to the relevant practice surface.
  "exam.weak_area_title": "Где подтянуть",
  "exam.weak_area_body": "В разделе «{section}» — {pct}%. Стоит подучить.",
  "exam.weak_area_cta_vocab": "К словам",
  "exam.weak_area_cta_grammar": "К грамматике",
  // Review answers screen — accessible from both pass + fail.
  "exam.result_cta_review": "Посмотреть ответы",
  "exam.review_title": "Разбор ответов",
  "exam.review_subtitle": "Итог: {pct}%",
  "exam.review_your_answer": "Ваш ответ",
  "exam.review_correct_answer": "Правильно",
  "exam.review_blank": "(пусто)",
  "exam.review_error": "Не удалось загрузить разбор экзамена.",

  /* ─── Admin: reset-to-level (debug) ────────── */
  // Row of CEFR-level buttons in Settings → Admin section. Sets
  // `currentLevel` and wipes LevelUpAttempt rows so an admin can
  // retake an exam without waiting 24 h cooldown.
  "settings.reset_level_title": "Сбросить уровень (отладка)",
  "settings.reset_level_hint": "Установить currentLevel и удалить попытки экзамена.",
  "settings.reset_level_confirm": "Сбросить на {level}? Все попытки экзамена будут удалены.",
  "settings.reset_level_done": "Уровень: {level} · удалено попыток: {n}",

  /* ─── Level names (CEFR) ─────────────────── */
  // A0 is informal "pre-A1"; the rest are standard CEFR. Names chosen
  // for short labels — they appear in space-constrained spots (level
  // hero, Levels page roadmap) where long names would wrap.
  "level.name_A0": "Стартер",
  "level.name_A1": "Начальный",
  "level.name_A2": "Базовый",
  "level.name_B1": "Средний",
  "level.name_B2": "Выше среднего",
  "level.name_C1": "Продвинутый",
  "level.name_C2": "Совершенный",

  /* ─── Levels page (hub + focus) ──────────── */
  "level_hub.title": "Ваш путь",
  "level_hub.currently_learning": "Сейчас учим",
  "level_hub.your_path": "Ваш маршрут",
  // `{words} слов · {rules} правил грамматики` — summary line in the current-level hero.
  "level_hub.totals": "{words} слов · {rules} правил грамматики",
  "level_hub.level_up_at_both": "85% на обоих — и дальше",
  "level_hub.cards_to_go": "~{n} карточек до следующего уровня",
  // Exam-agnostic phrasing while Phase 7 is deferred — see the
  // home.ready_to_advance comment for the migration plan.
  "level_hub.ready": "Готов к следующему уровню",
  "level_hub.continue": "Продолжить {level}",
  "level_hub.ring_vocab": "Слова",
  "level_hub.ring_grammar": "Грамматика",
  "level_hub.bar_vocab": "СЛОВА",
  "level_hub.bar_grammar": "ГРАММАТИКА",
  "level_hub.pill_complete": "пройден",
  "level_hub.pill_active": "текущий",
  "level_hub.n_cards": "{n} карточек",
  "level_hub.no_content": "пока нет",
  "level_focus.title_with_lang": "{level} · Румынский",
  "level_focus.vocab": "Слова",
  "level_focus.grammar": "Грамматика",
  // All three soft-pedal "exam" while Phase 7 is deferred — they describe
  // the readiness milestone without promising a specific exam UI.
  "level_focus.threshold_copy": "Наберите 85% на обоих, чтобы быть готовым к {next}",
  "level_focus.threshold_copy_last": "Финальный уровень. Поддерживайте 85% для сохранения мастерства.",
  "level_focus.ready_copy": "Вы готовы! Поддерживайте темп — переход на {next} скоро.",
  "level_focus.study_cards": "Учить {level}",
  "level_focus.browse_grammar": "Грамматика {level}",
  "level_focus.not_current_hint": "Вы сейчас на {current}. Завершите его, чтобы двигаться дальше.",

  /* ─── Grammar page ───────────────────────── */
  "grammar.title": "Грамматика",
  "grammar.totals": "{total} правил · {known} выучено · {learning} в процессе",
  "grammar.search_placeholder": "Поиск правил…",
  // "Up next" hero (Level mode) — focal card for the next suggested rule.
  "grammar.up_next": "Следующее",
  "grammar.read": "Открыть",
  "grammar.practice": "Тренировать",
  // All-known celebration card (replaces "Up next" when every rule at
  // the level is mature).
  "grammar.all_known_eyebrow": "Грамматика {level} пройдена",
  "grammar.all_known_title": "Грамматика {level} полностью освоена!",
  "grammar.all_known_body": "Все правила выучены. Сосредоточьтесь на лексике или загляните на страницу уровней.",
  "grammar.go_to_levels": "Перейти к уровням",
  "grammar.empty_no_match": "Нет правил, подходящих под «{q}».",
  "grammar.empty_filter": "Пока нет правил в этой категории.",
  "grammar.empty_all": "Пока нет правил. Импортируйте JSON, чтобы начать.",
  "grammar.filter_by_tag": "Фильтр по тегу:",
  "grammar.clear_filter": "Сбросить",

  /* ─── Progress page ──────────────────────── */
  "progress.title": "Прогресс",
  "progress.subtitle_all_time": "За всё время",
  "progress.stat_words_known": "Выучено слов",
  "progress.stat_learning": "В процессе",
  "progress.stat_day_streak": "Серия (дни)",
  "progress.stat_retention": "Удержание",
  "progress.delta_this_week": "+{n} за неделю",
  "progress.delta_avg_ease": "ср. EF {n}",
  "progress.delta_best_streak": "лучшая {n}",
  "progress.delta_last_30d": "за 30 дней",
  "progress.delta_none": "—",
  "progress.vocabulary": "Словарь",
  "progress.total_words": "{n} всего",
  "progress.legend_known": "Выучено",
  "progress.legend_learning": "В процессе",
  "progress.legend_new": "Новые",
  "progress.activity": "Активность",
  "progress.last_13_weeks": "Последние 13 недель",
  "progress.heatmap_less": "меньше",
  "progress.heatmap_more": "больше",
  "progress.section_by_level": "По уровням",
  "progress.view_level": "Подробнее",

  /* ─── Study flow ─────────────────────────── */
  "study.loading": "Загрузка…",
  "study.reveal": "Показать",
  "study.check": "Проверить",
  "study.type_prompt": "Ваш ответ",
  "study.type_placeholder": "Введите перевод…",
  "study.type_correct": "Правильно",
  "study.type_empty": "(пусто)",
  "study.tap_or_space": "Коснитесь карточки или нажмите",
  "study.play_audio": "Прослушать произношение",
  "study.mcq_pick": "выберите один",
  "study.grade_again": "Снова",
  "study.grade_hard": "Сложно",
  "study.grade_good": "Нормально",
  "study.grade_easy": "Легко",
  "study.know_action": "Я уже знаю это слово",
  // Same affordance, different noun for grammar — "тему" (topic) reads
  // honestly when rating a rule rather than a vocabulary item.
  "study.know_action_grammar": "Я уже знаю эту тему",
  "study.know_armed_prompt": "Вы уверены?",
  "study.know_saving": "Сохранение…",
  "study.direction_ro_to_ru": "RO → RU",
  "study.direction_ru_to_ro": "RU → RO",
  "study.badge_type": "ввести",
  "study.badge_grammar": "Грамматика",
  "study.badge_new": "Новое",
  // Quiz card (Phase 3a — CLOZE renderer).
  "study.quiz_choose_eyebrow": "Выберите правильную форму",
  "study.quiz_type_eyebrow": "Введите правильную форму",
  "study.quiz_input_placeholder": "Введите ответ…",
  "study.quiz_check": "Проверить",
  "study.quiz_skip": "Пропустить",
  "study.quiz_correct_label": "Верно",
  // Wrong-answer label — shows the expected answer inline.
  "study.quiz_correct_answer": "Правильный ответ: {answer}",
  "study.quiz_continue": "Продолжить",
  // Defensive fallback for unsupported quiz kinds (future task types
  // before their phase ships). CLOZE + TRANSFORM are wired today.
  "study.quiz_unsupported_kind": "Этот тип задачи скоро появится.",
  // Transform quiz (Phase 3b). Source-card + arrow + target-card
  // layout. Eyebrow is generic across both modes — the per-card
  // "what to transform into" lives in `hints[]`.
  "study.quiz_transform_eyebrow": "Преобразуйте предложение",
  "study.quiz_transform_source": "Исходное",
  "study.quiz_transform_target": "Результат",
  "study.synonyms_short": "син:",
  "study.all_caught_up_title": "Всё повторено",
  "study.all_caught_up_body":
    "Сейчас нет карточек к повторению. Возвращайтесь, когда появятся новые, или добавьте больше слов.",
  "study.refresh": "Обновить",
  "study.back_to_home": "На главную",

  /* ─── Login ──────────────────────────────── */
  "login.title": "Вход",
  "login.welcome_back": "С возвращением",
  "login.subtitle": "Войдите, чтобы продолжить серию занятий.",
  "login.email": "Электронная почта",
  "login.password": "Пароль",
  "login.submit": "Войти",
  "login.submitting": "Входим…",
  "login.error_invalid": "Неверная почта или пароль",
  "login.error_generic": "Не удалось войти",
  "login.need_account": "Нужен аккаунт?",
  "login.contact_admin": "Обратитесь к администратору",
  // Replaces the "contact admin" link copy now that self-service
  // signup is shipped. Kept `contact_admin` for back-compat in case
  // any other surface still uses it.
  "login.sign_up": "Зарегистрируйтесь",
  "login.forgot_password": "Забыли пароль?",

  /* ─── Forgot password ────────────────────── */
  "forgot.title": "Восстановление пароля",
  "forgot.subtitle": "Введите адрес — пришлём ссылку для сброса.",
  "forgot.email": "Электронная почта",
  "forgot.submit": "Отправить ссылку",
  "forgot.submitting": "Отправляем…",
  "forgot.back_to_login": "Вернуться ко входу",
  "forgot.sent_title": "Проверьте почту",
  // Variable {email} is replaced at render time.
  "forgot.sent_body":
    "Если у нас есть аккаунт на адрес {email}, мы отправили ссылку для сброса пароля. Ссылка действует 60 минут.",
  "forgot.sent_resend": "Не пришло? Отправить снова",

  /* ─── Reset password ─────────────────────── */
  "reset.title": "Новый пароль",
  "reset.subtitle": "Придумайте надёжный пароль — минимум 8 символов.",
  "reset.password": "Новый пароль",
  "reset.password_placeholder": "минимум 8 символов",
  "reset.confirm_password": "Повторите пароль",
  "reset.submit": "Сохранить пароль",
  "reset.submitting": "Сохраняем…",
  "reset.success_toast": "Пароль обновлён — войдите снова.",
  "reset.error_invalid_link": "Ссылка недействительна или истекла",
  "reset.invalid_link_title": "Ссылка не работает",
  "reset.invalid_link_body": "Возможно, ссылка устарела или ею уже воспользовались. Попробуйте запросить новую.",
  "reset.request_new_link": "Запросить новую ссылку",

  /* ─── Verify email ───────────────────────── */
  "verify.pending_title": "Подтверждаем почту…",
  "verify.pending_body": "Минутку — мы проверяем ссылку из письма.",
  "verify.success_title": "Почта подтверждена",
  "verify.success_body": "Спасибо! Ваш аккаунт активен. Можно возвращаться к занятиям.",
  "verify.success_cta": "На главную",
  "verify.error_title": "Ссылка не работает",
  "verify.error_body": "Похоже, ссылка устарела или ею уже воспользовались. Войдите в аккаунт и запросите новую.",
  "verify.error_cta": "Войти и запросить заново",
  "verify.resend": "Отправить ссылку повторно",
  "verify.resend_sent": "Письмо отправлено — проверьте почту.",
  "verify.resend_error": "Не удалось отправить — попробуйте позже.",

  /* ─── Validation ─────────────────────────── */
  // These keys match the strings inside `VALIDATION_KEYS` in
  // `packages/shared/src/auth-errors.ts`. The Zod schemas store the
  // key as the issue message; <FieldError> looks it up via useT() so
  // the message renders in the user's locale.
  "validation.required": "Поле обязательно",
  "validation.email_invalid": "Неверный формат email",
  "validation.password_too_short": "Минимум 8 символов",
  "validation.password_too_long": "Слишком длинный пароль",
  "validation.passwords_must_match": "Пароли не совпадают",
  "validation.token_invalid": "Неверная ссылка",

  /* ─── Auth error codes ───────────────────── */
  // Match the values in `AUTH_ERROR_CODES`. The backend returns the
  // code in the error body; the web localizes via these keys.
  "auth.errors.invalid_credentials": "Неверная почта или пароль",
  "auth.errors.email_already_registered": "Такой email уже зарегистрирован",
  "auth.errors.reset_link_invalid": "Ссылка для сброса недействительна или истекла",
  "auth.errors.verify_link_invalid": "Ссылка для подтверждения недействительна или истекла",
  // {seconds} is interpolated from the body's retryAfterSec.
  "auth.errors.rate_limited": "Слишком много попыток. Попробуйте через {seconds} с.",
  "auth.errors.unauthorized": "Требуется авторизация",
  // Fallback rendered when the server returns an error without a
  // recognised code — keeps the UI from leaking raw HTTP messages.
  "auth.errors.unknown": "Что-то пошло не так. Попробуйте позже.",

  /* ─── Sign up ────────────────────────────── */
  "signup.title": "Создать аккаунт",
  "signup.subtitle": "Регистрация — и серия занятий начинается.",
  "signup.name": "Имя",
  "signup.name_placeholder": "Как к тебе обращаться",
  "signup.email": "Электронная почта",
  "signup.password": "Пароль",
  "signup.password_placeholder": "минимум 8 символов",
  "signup.confirm_password": "Повторите пароль",
  "signup.submit": "Зарегистрироваться",
  "signup.submitting": "Создаём…",
  "signup.have_account": "Уже есть аккаунт?",
  "signup.sign_in": "Войти",

  /* ─── Settings ───────────────────────────── */
  "settings.title": "Настройки",
  "settings.section_admin": "Администрирование",
  "settings.admin_link_title": "Админ-панель",
  "settings.admin_link_sub": "Импорт слов и правил грамматики",
  "settings.section_account": "Аккаунт",
  "settings.name_label": "Имя",
  "settings.name_placeholder": "Не указано",
  "settings.signed_in_as": "Вы вошли как",
  "settings.section_appearance": "Внешний вид",
  "settings.theme_light": "Светлая",
  "settings.theme_dark": "Тёмная",
  "settings.theme_system": "Системная",
  "settings.section_language": "Язык интерфейса",
  "settings.language_ru": "Русский",
  "settings.language_ro": "Română",
  "settings.section_study_mode": "Режим занятий",
  "settings.study_mode_title": "Открытый или направленный",
  "settings.study_mode_hint": "Открытый — все уровни вперемешку. Уровень — пошаговый путь. Прогресс общий.",
  "settings.mode_open_label": "Открытый",
  "settings.mode_open_sub": "Все уровни",
  "settings.mode_level_label": "Уровень",
  "settings.mode_level_sub": "Сейчас {level}",
  "settings.section_current_level": "Текущий уровень",
  "settings.current_level_title": "Где вы сейчас",
  "settings.current_level_hint":
    "Можно переключиться вручную — например, чтобы повторить более ранний уровень или пропустить уже пройденный.",
  // {current} = currently selected level (e.g. "A0"); {next} = where the user is jumping to
  // (e.g. "A2"). Browser confirm() returns true on OK / false on Cancel.
  "settings.level_confirm":
    "Перейти с {current} на {next}? Прогресс не теряется — можно вернуться обратно в любой момент.",
  "settings.section_study": "Занятия",
  "settings.daily_batch_title": "Новых слов в день",
  "settings.daily_batch_hint": "Сколько новых слов вводить каждый день.",
  "settings.new_grammar_title": "Новых правил в день",
  "settings.new_grammar_hint": "Грамматика — это концепции, а не карточки. Лучше осваивать постепенно. 0 — пауза.",
  "settings.new_quiz_title": "Новых упражнений в день",
  "settings.new_quiz_hint": "Тренировки по правилам, которые вы уже видели. 0 — пауза.",
  "settings.section_reminders": "Напоминания",
  "settings.push_title": "Push-уведомления",
  "settings.push_status_enabled_here": "Включены на этом устройстве",
  "settings.push_status_plus_others": " · ещё {n}",
  "settings.push_status_only_others": "Включены на {n} другом устройстве",
  "settings.push_status_only_others_plural": "Включены на {n} устройствах",
  "settings.push_status_not_enabled": "Пока не включены.",
  "settings.push_status_https": "Требуется HTTPS (или localhost в разработке).",
  "settings.push_status_unsupported": "Ваш браузер не поддерживает push.",
  "settings.push_status_unconfigured": "Ключи на сервере ещё не настроены.",
  "settings.push_enable": "Включить напоминания",
  "settings.push_enabling": "Включение…",
  "settings.push_disable": "Отключить на этом устройстве",
  "settings.push_disabling": "Отключение…",
  "settings.push_send_test": "Отправить тестовое",
  "settings.push_sending_test": "Отправка…",
  "settings.push_test_result": "Отправлено: {delivered} из {attempted}",
  "settings.push_test_none_attempted": "Нет подписок — включите напоминания сначала",
  "settings.push_test_none_delivered": "Доставлено 0 из {attempted} — проверьте логи сервера",
  "settings.push_requires_https": "Для push нужен защищённый контекст (HTTPS или localhost).",
  "settings.push_unavailable": "Push недоступен в этом браузере.",
  "settings.push_server_unconfigured": "Сервер ещё не настроен — выполните",
  "settings.section_install": "Установка приложения",
  "settings.sign_out": "Выйти",
  "settings.save_failed": "Не удалось сохранить",

  /* ─── PWA update prompt ──────────────────── */
  "pwa.update_available": "Доступно обновление",
  "pwa.update_hint": "Перезагрузите, чтобы получить последнюю версию.",
  "pwa.reload": "Обновить",
  "pwa.dismiss": "Закрыть",

  /* ─── PWA install prompt ─────────────────── */
  "pwa.install_title": "Установить rmnlng",
  "pwa.install_hint": "Быстрый доступ прямо с домашнего экрана.",
  "pwa.install_action": "Установить",
  "pwa.install_ios_title": "Добавить на главный экран",
  // Split into "before"/"after" so the renderer can drop the inline
  // Share icon between them. Renders as: "Нажмите [share-icon] и
  // выберите «На экран „Домой"»."
  "pwa.install_ios_hint_before": "Нажмите ",
  "pwa.install_ios_hint_after": ' и выберите «На экран „Домой"».',

  // ─── Skills mode (scenarios) ──────────────────────────────────
  // Доктрина: scenarios.md. Промис формулируется как «после этого
  // ты сможешь…», все ярлыки на сценариях должны соответствовать
  // тому же вокабуляру.
  "nav.skills": "Умения",
  "settings.mode_scenarios_label": "Сценарии",
  "settings.mode_scenarios_sub": "Коммуникативные навыки",
  "skills.title": "Журнал способностей",
  "skills.hero_label": "Я могу",
  "skills.hero_count_of": "из {total} умений",
  "skills.continue": "Продолжить",
  "skills.submit_checkpoint": "Сдать чекпоинт",
  "skills.start_scenario": "Начать сценарий",
  "skills.start_anyway": "Начать всё равно",
  "skills.section_in_progress": "В процессе",
  "skills.section_ready_check": "Готов к чекпоинту",
  "skills.section_available": "Готово начать",
  "skills.section_soft_locked": "Можно, но сложно",
  "skills.section_locked": "Заблокировано",
  "skills.section_completed": "Освоено",
  "skills.detail_promise_label": "После этого ты сможешь",
  "skills.detail_words": "Слова",
  "skills.detail_rules": "Правила",
  "skills.detail_constructions": "Конструкции",
  "skills.detail_words_known": "{known} / {total} знакомы",
  "skills.detail_rules_open": "{open} / {total} открыты",
  "skills.detail_constructions_count": "{count} готовых фраз",
  "skills.detail_words_need": "Нужно ещё {n}",
  "skills.detail_words_ready": "✓ готово",
  "skills.detail_checkpoint_hint": "{count} заданий, миксующих эти слова и правила.",
  "skills.detail_progress_minutes": "~{n} мин",
  "skills.detail_capstone": "Капстоун секции",
  "skills.locked_why_title": "Откроется после",
  "skills.locked_explain_title": "Заблокировано",
  "skills.locked_status_done": "Освоено",
  "skills.locked_status_ready_check": "Готов к чекпоинту",
  "skills.locked_status_in_progress": "В процессе",
  "skills.locked_status_available": "Готово начать",
  "skills.locked_status_soft_locked": "Можно, но сложно",
  "skills.locked_status_locked": "Заблокировано",
  "skills.locked_back": "Понятно",
  "skills.empty_no_scenarios": "Сценарии скоро появятся",
  "skills.empty_no_started": "Начни с любого сценария ниже — это твой первый шаг.",
  "skills.soft_lock_warning_title": "Будет тяжело",
  "skills.soft_lock_warning_hint":
    "Тебе знакомо мало слов. Можешь начать всё равно — но рекомендуем сначала подкачать словарь.",
  "skills.scenario_meta": "{words} слов · {rules} правил · ~{mins} мин",
  "skills.locked_meta_after": "после: {names}",
  "skills.continue_in_progress": "В процессе",
  "skills.continue_ready_check": "✓ готов к чекпоинту",
  // Session UI strings (Phase 5).
  "skills.session_topbar_label": "сценарий",
  "skills.session_step_word": "Слова · {idx} / {total}",
  "skills.session_step_rule": "Правило · {idx} / {total}",
  "skills.session_step_line": "Фраза · {idx} / {total}",
  "skills.session_step_mini_check": "Мини-проверка · {idx} / {total}",
  "skills.session_continue": "Дальше",
  "skills.session_next": "Следующее",
  "skills.session_understood": "Понятно — дальше",
  "skills.session_to_practice": "К проверке →",
  "skills.session_practice_label": "Проверка",
  "skills.session_open_full": "Открыть полный разбор",
  "skills.session_check": "Проверить",
  "skills.session_skip": "Пропустить",
  "skills.session_translate_to_ro": "Переведи на румынский",
  "skills.session_input_placeholder": "введи фразу",
  "skills.session_correct": "Правильно",
  "skills.session_wrong": "Не так — правильно: {answer}",
  "skills.session_rule_summary": "Краткая суть",
  "skills.session_rule_proof": "Проверим",
  "skills.session_quick_check": "Быстрая проверка",
  "skills.session_word_known_skip": "Уже знаешь — пропускаем",
  // Material-collected bridge.
  "skills.material_title": "Материал собран",
  "skills.material_subtitle": "Слова, правила и фразы для «{title}» в твоей голове.",
  "skills.material_words_collected": "слов",
  "skills.material_rules_collected": "правил",
  "skills.material_lines_collected": "фраз",
  "skills.material_take_checkpoint": "Сдать чекпоинт · ~{mins} мин",
  "skills.material_later": "Позже",
  // Checkpoint UI strings (Phase 6).
  "skills.checkpoint_label": "чекпоинт",
  "skills.checkpoint_progress": "{idx} / {total}",
  "skills.checkpoint_loading": "Готовим вопросы…",
  "skills.checkpoint_empty": "Для этого сценария ещё не готов чекпоинт.",
  "skills.checkpoint_exit_confirm": "Выйти? Прогресс не сохранится.",
  "skills.checkpoint_submit": "Сдать ответы",
  "skills.checkpoint_select_answer": "Выбери вариант",
  "skills.checkpoint_type_answer": "Введи ответ",
  "skills.checkpoint_translation_label": "Перевод",
  "skills.checkpoint_source_label": "Из",
  // Result screens.
  "skills.result_passed_title": "Сдал!",
  "skills.result_passed_score": "{correct} из {total} · {pct}%",
  "skills.result_passed_subtitle": "Сценарий освоен. Теперь ты можешь:",
  "skills.result_passed_cta_next": "Что дальше",
  "skills.result_passed_cta_journal": "В журнал",
  "skills.result_borderline_title": "Можно засчитать",
  "skills.result_borderline_subtitle":
    "{correct} из {total}. Решай: укрепить слабое место сейчас или закрыть и вернуться позже.",
  "skills.result_borderline_weak": "Слабые места",
  "skills.result_borderline_accept": "Засчитать как освоенный",
  "skills.result_borderline_repeat": "Повторить эту тему",
  "skills.result_failed_title": "Почти — {correct} / {total}",
  "skills.result_failed_subtitle": "Ещё одна сессия — и закроем.",
  "skills.result_failed_weak": "Слабые места",
  "skills.result_failed_cta_resume": "Добить слабые места",
  "skills.result_failed_cta_break": "Перерыв · вернусь позже",
  "skills.result_review_correct": "Правильно",
  "skills.result_review_wrong": "Неверно",
  "skills.result_review_expected": "Правильно: {answer}",
  // Now-you-can.
  "skills.now_you_can_title": "Теперь ты можешь",
  "skills.now_you_can_unlocked": "Открылось",
  "skills.now_you_can_continue": "Продолжить путь",
  "skills.now_you_can_journal": "Вернуться в журнал",
  // Tree (Map) + Stall (Phase 7).
  "skills.tree_title": "Карта",
  "skills.tree_back": "← В журнал",
  "skills.tree_stage": "Стадия {n}",
  "skills.tree_legend_completed": "Освоено",
  "skills.tree_legend_in_progress": "В процессе",
  "skills.tree_legend_available": "Готово",
  "skills.tree_legend_locked": "Заблокировано",
  "skills.tree_journal_link": "Карта пути →",
  "skills.stall_title": "Пауза в скиллах",
  "skills.stall_subtitle": "Все доступные сценарии освоены. Подкачай словарь — и откроются новые.",
  "skills.stall_domain_have": "{have} / {need} слов знакомо",
  "skills.stall_domain_unlocks": "откроет {n} {label}",
  "skills.stall_domain_unlocks_one": "сценарий",
  "skills.stall_domain_unlocks_few": "сценария",
  "skills.stall_domain_unlocks_many": "сценариев",
  "skills.stall_domain_cta": "Учить",
  "skills.stall_switch_mode": "или переключись в открытый режим",
  // NEEDS_REVIEW.
  "skills.section_needs_review": "Освежить",
  "skills.needs_review_meta": "Прошло время, повтори",
  // Home (Skills mode body) + ModeSwitch3.
  "home_skills.next_up": "Готово начать",
  "home_skills.journal_link": "Журнал",
  "home_skills.empty_journal": "Открыть журнал →",
  "home_skills.start_here": "Начни отсюда",
  "home_skills.start_cta": "Начать сценарий",
  "home_skills.mini_map_title": "Твой путь",
  "home_skills.mini_map_open_full": "Вся карта",
  "home_skills.mode_hint_title": "Skills mode",
  "home_skills.mode_hint_body":
    "Учишься через коммуникативные сценарии. Слова и правила приходят как материалы под цель.",
  "mode_switch.label_open": "Open",
  "mode_switch.sub_open": "Free",
  "mode_switch.sub_level": "Level",
  "mode_switch.sub_skills": "Skills",
  // Inline labels — short tags that surface in multiple places.
  "skills.tag_capstone": "капстоун",
  "skills.tag_scenario": "сценарий",
  "skills.tag_checkpoint": "Чекпоинт",
  "skills.tag_journal_back": "← Журнал",
  "skills.tag_pause": "пауза",
  "skills.tag_soft_locked_inline": "сложновато",
  // Weak-rule card on borderline result.
  "skills.weak_rule_badge": "пр",
  "skills.weak_rule_errors_one": "{n} ошибка",
  "skills.weak_rule_errors_few": "{n} ошибки",
  "skills.weak_rule_errors_many": "{n} ошибок",
  // Step-strip glyphs (single-letter abbreviations of С / П / Ф).
  "skills.step_glyph_word": "С",
  "skills.step_glyph_rule": "П",
  "skills.step_glyph_chunk": "Ф",
  "skills.rule_popup_close": "Закрыть и продолжить",
} as const;
